import type { GetStaticProps, NextPage } from 'next';
import { z } from 'zod';
import { ModularPageWrapper } from '../../components/ModularPageWrapper';
import { modularPageWrapperSchema } from '../../components/ModularPageWrapper/schema';
import { serverLogger } from '../../utils/loggers/server';
import { getCachedEntryByUrl } from '../../utils/requests/contentstack/cachedRequests';

export type CareersPageProps = z.infer<typeof modularPageWrapperSchema>;

export const getStaticProps: GetStaticProps<CareersPageProps> = async () => {
  try {
    const entryResponse = await getCachedEntryByUrl({
      contentType: 'modular_page',
      url: '/careers',
      schema: modularPageWrapperSchema,
      logger: serverLogger,
    });

    return {
      props: {
        seoData: entryResponse.data.seoData,
        modules: entryResponse.data.modules,
      },
      // revalidate configures the cache policy, this sets it to cache for 5 minutes
      // https://nextjs.org/docs/pages/building-your-application/deploying/production-checklist#caching
      revalidate: 300,
    };
  } catch (error: unknown) {
    serverLogger({
      level: 'error',
      team: 'client-onboarding',
      message: 'Failed to fetch required page data - /careers',
      context: {
        error,
        page: '/careers',
        severity: 'critical',
      },
    });

    throw new Error('Required data not provided to render /careers page');
  }
};

const CareersPage: NextPage<CareersPageProps> = ({ seoData, modules }) => {
  return (
    <ModularPageWrapper
      viewEventTrackingName="corporate"
      seoData={seoData}
      modules={modules}
    />
  );
};

export default CareersPage;
